/**
 * Store ("STORE"): HEADS and BALLS (buy with coins; the synced wallet spends server-side via POST /shop/unlock once
 * the account has synced) and LOCKER (everything you own; equip a head as your default pick and a ball skin). COINS:
 * the five coin packs via Paddle overlay checkout (sandbox), tab shown ONLY in a plain web browser (`paddleWebAllowed`);
 * while `WEB_COIN_PURCHASES_ENABLED` is off the tab shows `WEB_COINS_NOTE` only (no prices, no buy buttons, no
 * checkout); coins are cosmetic-only and credited by the server webhook. Mirrors apps/mobile/src/ui/screens/store.
 * Prices / rarity come from `@hf/shared` catalogs. Pad / keyboard: ◀ ▶ tabs, A opens the grid, A buys / equips.
 */
import './store.css';
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  BALL_SKIN_CATALOG,
  BALL_SKIN_IDS,
  CHARACTERS,
  COIN_PACKS,
  WEB_COINS_NOTE,
  catalogFor,
  ballSkinCatalogFor,
  type BallSkinId,
  type CharacterId,
} from '@hf/shared';
import { track } from '@hf/client-core/analytics';
import { draft, updateDraft } from '@hf/client-core/ui-model/flow';
import { useActions, useServices } from '../actions';
import { Art, BallSkinArt, CoinBalance, CoinIcon, Head, LockBadge, RarityTag } from '../components';
import { LAYER, useFocusGrid } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { thousands } from '@hf/client-core/ui-model/format';
import { equippedBallSkin, setEquippedBallSkin, unlockSku, wallet, walletBalance } from '../wallet';
import { storeReturn, storeTab, type StoreTab } from './store-nav';
import { openCoinCheckout, paddleWebAllowed, webCoinPurchasesEnabled } from '../paddle';
import { packKey } from '../assets';

type Tab = StoreTab;
const TABS: readonly { id: Tab; label: string }[] = [
  { id: 'heads', label: 'HEADS' },
  { id: 'balls', label: 'BALLS' },
  { id: 'locker', label: 'LOCKER' },
  // Web-only (Apple 3.1.1): never shown inside a native app shell.
  ...(paddleWebAllowed() ? [{ id: 'coins' as const, label: 'COINS' }] : []),
];

export function Store() {
  const actions = useActions();
  const { audio } = useServices();
  const tab = storeTab.value;
  const setTab = (t: Tab): void => {
    storeTab.value = t;
  };
  const [zone, setZone] = useState<'tabs' | 'panel'>('tabs');
  const [buying, setBuying] = useState<ShopCard | null>(null);
  const [packMsg, setPackMsg] = useState<string | null>(null);
  const packsOn = webCoinPurchasesEnabled();
  const packs = packsOn ? COIN_PACKS : [];
  const buyPack = async (packId: string): Promise<void> => {
    if (!packsOn) return;
    playSfx(audio, UI_SFX.confirm);
    const r = await openCoinCheckout(packId);
    setPackMsg(
      r === 'opened'
        ? null
        : r === 'signed_out'
          ? 'Sign in first so the coins land on your account.'
          : 'Checkout is unavailable right now. Try again in a moment.',
    );
  };
  useEffect(() => {
    track('store_viewed', {});
    track('shop_viewed', {
      entry:
        storeReturn.peek() === 'character_select'
          ? 'low_coins'
          : storeReturn.peek() === 'results'
            ? 'post_match'
            : 'menu',
    });
  }, []);
  const balance = walletBalance.value;
  const equipped = equippedBallSkin.value;
  const equippedHead = draft.value.characters[0];

  const back = (): void => {
    playSfx(audio, UI_SFX.back);
    actions.goto(storeReturn.peek());
  };

  const tabs = useFocusGrid({
    count: TABS.length,
    initial: TABS.findIndex((t) => t.id === tab),
    active: zone === 'tabs' && !buying,
    horizontal: true,
    priority: LAYER.SCREEN,
    onMove: (i) => {
      setTab(TABS[i]!.id);
      playSfx(audio, UI_SFX.move);
    },
    onConfirm: (i) => {
      setTab(TABS[i]!.id);
      setZone('panel');
      playSfx(audio, UI_SFX.confirm);
    },
    onBack: back,
  });

  const headCard = (id: CharacterId): ShopCard => {
    const c = CHARACTERS.find((x) => x.id === id)!;
    const cat = catalogFor(id);
    return {
      id,
      kind: 'head',
      name: c.name.replace('THE ', ''),
      owned: wallet.owns(id),
      equipped: equippedHead === id,
      price: cat.priceCoins,
      rarity: cat.rarity,
      art: <Head character={id} class="hf-shop-card__head" />,
    };
  };
  const ballCard = (id: BallSkinId): ShopCard => {
    const cat = ballSkinCatalogFor(id);
    return {
      id,
      kind: 'ball',
      name: cat.name,
      owned: wallet.owns(id),
      equipped: equipped === id,
      price: cat.priceCoins,
      rarity: cat.rarity,
      art: <BallSkinArt skin={id} class="hf-shop-card__ball" />,
    };
  };
  const heads = CHARACTERS.map((c) => headCard(c.id));
  const balls = BALL_SKIN_IDS.map(ballCard);
  const cards =
    tab === 'heads'
      ? heads
      : tab === 'balls'
        ? balls
        : tab === 'coins'
          ? []
          : [...heads, ...balls].filter((c) => c.owned);

  const equip = (c: ShopCard): void => {
    if (c.kind === 'ball') setEquippedBallSkin(c.id as BallSkinId);
    else {
      const cur = draft.peek().characters;
      updateDraft({ characters: [c.id as CharacterId, cur[1]] });
      track('item_equipped', { item_id: c.id, item_type: 'head' });
    }
    playSfx(audio, UI_SFX.confirm);
  };
  const choose = (c: ShopCard | undefined): void => {
    if (!c) return;
    if (c.owned) equip(c);
    else {
      setBuying(c);
      playSfx(audio, UI_SFX.confirm);
    }
  };

  const panel = useFocusGrid({
    count: tab === 'coins' ? packs.length : cards.length,
    active: zone === 'panel' && !buying,
    priority: LAYER.SCREEN,
    onMove: () => playSfx(audio, UI_SFX.move),
    onConfirm: (i) => (tab === 'coins' ? packs[i] && void buyPack(packs[i].id) : choose(cards[i])),
    onBack: () => {
      setZone('tabs');
      playSfx(audio, UI_SFX.back);
    },
  });

  // Entering a tab's grid focuses card 0; keep the top of the page (featured hero) in view instead of nudging past it.
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (panel.index === 0 && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [tab, zone]);

  return (
    <div class="hf-screen hf-store" data-screen="store">
      <header class="hf-store__top">
        <button class="hf-back interactive" onClick={back} aria-label="Back">
          ◀
        </button>
        <h1 class="hf-store__title t-display">STORE</h1>
        <CoinBalance balance={balance} class="hf-store__balance" />
      </header>

      <nav class="hf-store__tabs" role="tablist" aria-label="Store sections">
        {TABS.map((t, i) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            data-testid={`store-tab-${t.id}`}
            class={`hf-store__tab interactive ${tab === t.id ? 'selected' : ''}`}
            {...tabs.item(i)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div class="hf-store__body" ref={bodyRef}>
        {tab === 'heads' ? (
          <FeaturedCard card={heads.find((h) => h.id === FEATURED_HEAD)!} onChoose={choose} />
        ) : null}
        {tab === 'coins' && !packsOn ? (
          <div class="hf-store__empty" data-testid="store-coins-web-note">
            {WEB_COINS_NOTE}
          </div>
        ) : tab === 'coins' ? (
          <div class="hf-shop-grid hf-shop-grid--coins" role="list" data-testid="store-coin-packs">
            {packs.map((p, i) => (
              <button
                key={p.id}
                data-testid={`store-pack-${p.id}`}
                class={`hf-shop-card hf-shop-card--r${Math.min(4, i + 1)} interactive locked ${
                  panel.index === i && zone === 'panel' ? 'focused' : ''
                }`}
                {...panel.item(i)}
              >
                <div class="hf-shop-card__stage">
                  {p.badge ? (
                    <span class="hf-shop-card__rarity hf-pack-badge t-broadcast">{p.badge}</span>
                  ) : null}
                  <Art
                    keys={[packKey(p.id)]}
                    alt={`${p.name} coin pack`}
                    class="hf-pack-art"
                    fallback={<CoinIcon class="hf-pack-art hf-pack-art--coin" />}
                  />
                </div>
                <div class="hf-shop-card__name t-display">{p.name}</div>
                <div class="hf-pack-coins t-display">
                  {thousands(p.coins)} coins{p.bonusPct ? ` · +${p.bonusPct}%` : ''}
                </div>
                <div class="hf-shop-card__cta hf-shop-card__cta--buy t-display">${p.usd.toFixed(2)}</div>
              </button>
            ))}
          </div>
        ) : cards.length === 0 ? (
          <div class="hf-store__empty">Nothing in your locker yet.</div>
        ) : (
          <div class={`hf-shop-grid ${tab === 'locker' ? 'hf-shop-grid--locker' : ''}`} role="list">
            {cards.map((c, i) => (
              <button
                key={c.id}
                data-testid={`store-card-${c.id}`}
                class={`hf-shop-card hf-shop-card--r${c.rarity} interactive ${c.owned ? 'owned' : 'locked'} ${c.equipped ? 'equipped' : ''} ${
                  panel.index === i && zone === 'panel' ? 'focused' : ''
                }`}
                {...panel.item(i)}
              >
                <div class="hf-shop-card__stage">
                  <RarityTag rarity={c.rarity} class="hf-shop-card__rarity" />
                  {!c.owned ? <LockBadge class="hf-shop-card__lock" /> : null}
                  {c.art}
                </div>
                <div class="hf-shop-card__name t-display">{c.name}</div>
                <CardAction card={c} />
              </button>
            ))}
          </div>
        )}
      </div>
      <div class="hf-store__note">
        {tab === 'balls'
          ? 'Cosmetic only · same physics · the ball wears the skin of whoever has possession.'
          : tab === 'coins' && !packsOn
            ? 'Earn coins by playing: CPU and online matches pay out. Spend them on heads and ball skins.'
            : tab === 'coins'
              ? (packMsg ??
                'Coins buy cosmetics only (heads, ball skins) · no gameplay advantage · secure checkout by Paddle (sandbox test mode).')
              : tab === 'locker'
                ? 'Equipped head is your default pick at kickoff · equipped ball skin shows in every match.'
                : 'Earn coins by playing: CPU and online matches pay out. Ranked normalizes every head.'}
      </div>
      {buying ? (
        <BuyDialog
          card={buying}
          balance={balance}
          onClose={() => setBuying(null)}
          onBuy={() => {
            const ok = unlockSku(buying.id, buying.price);
            playSfx(audio, ok ? UI_SFX.confirm : UI_SFX.error);
            if (ok) {
              equip({ ...buying, owned: true });
              setBuying(null);
            }
          }}
        />
      ) : null}
    </div>
  );
}

/** The hero spot at the top of HEADS: the legendary head, big. Cosmetic only, like everything in the store. */
const FEATURED_HEAD: CharacterId = 'lodestone';

function CardAction({ card: c }: { card: ShopCard }) {
  if (!c.owned)
    return (
      <div class={`hf-shop-card__cta hf-shop-card__cta--buy hf-rarity-${c.rarity} t-display`}>
        <CoinIcon class="hf-shop-card__coin" />
        {thousands(c.price)}
      </div>
    );
  return c.equipped ? (
    <div class="hf-shop-card__cta hf-shop-card__cta--equipped t-display">✓ EQUIPPED</div>
  ) : (
    <div class="hf-shop-card__cta hf-shop-card__cta--equip t-display">EQUIP</div>
  );
}

function FeaturedCard({ card: c, onChoose }: { card: ShopCard; onChoose(c: ShopCard): void }) {
  const def = CHARACTERS.find((x) => x.id === c.id)!;
  return (
    <section class={`hf-store-feature hf-shop-card--r${c.rarity}`} data-testid="store-featured">
      <div class="hf-store-feature__art">
        <Head character={c.id as CharacterId} class="hf-store-feature__head" />
      </div>
      <div class="hf-store-feature__info">
        <div class="hf-store-feature__kicker t-broadcast">★ FEATURED</div>
        <RarityTag rarity={c.rarity} class="hf-store-feature__rarity" />
        <h2 class="hf-store-feature__name t-display">{def.name}</h2>
        <p class="hf-store-feature__bio">{def.bio}</p>
        <button
          type="button"
          class={`hf-store-feature__cta interactive ${c.owned ? (c.equipped ? 'equipped' : 'owned') : ''} t-display`}
          data-testid="store-featured-cta"
          onClick={() => onChoose(c)}
        >
          {c.owned ? (
            c.equipped ? (
              '✓ EQUIPPED'
            ) : (
              'EQUIP'
            )
          ) : (
            <>
              <CoinIcon class="hf-shop-card__coin" />
              UNLOCK · {thousands(c.price)}
            </>
          )}
        </button>
        <div class="hf-store-feature__fine">Ranked normalizes every head · earn coins by playing</div>
      </div>
    </section>
  );
}

function BuyDialog(p: { card: ShopCard; balance: number; onBuy(): void; onClose(): void }) {
  const short = p.card.price - p.balance;
  const can = short <= 0;
  const grid = useFocusGrid({
    count: can ? 2 : 1,
    cols: can ? 2 : 1,
    horizontal: true,
    active: true,
    priority: LAYER.MODAL,
    onConfirm: (i) => (can && i === 1 ? p.onBuy() : p.onClose()),
    onBack: p.onClose,
  });
  return (
    <div
      class="hf-store__dlg-back"
      role="dialog"
      aria-modal="true"
      aria-label="Unlock"
      data-testid="store-buy-dialog"
    >
      <div class="hf-dlg hf-dlg--sm">
        <div class="hf-store__dlg-art">{p.card.art}</div>
        <h2 class="t-display hf-dlg__title">UNLOCK {p.card.name}?</h2>
        <p class="hf-dlg__body">
          {can ? (
            <>
              Costs <b>{thousands(p.card.price)}</b> coins · you have {thousands(p.balance)}.
            </>
          ) : (
            <>Not enough coins: you need {thousands(short)} more. Win CPU and online matches to earn them.</>
          )}
        </p>
        <div class="hf-dlg__actions">
          <button
            type="button"
            class="hf-btn hf-btn--ghost interactive"
            {...grid.item(0)}
            onClick={p.onClose}
          >
            {can ? 'CANCEL' : 'OK'}
          </button>
          {can ? (
            <button
              type="button"
              class="hf-btn hf-btn--gold interactive"
              data-testid="store-buy-confirm"
              {...grid.item(1)}
              onClick={p.onBuy}
            >
              BUY · {thousands(p.card.price)}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface ShopCard {
  readonly id: CharacterId | BallSkinId;
  readonly kind: 'head' | 'ball';
  readonly name: string;
  readonly owned: boolean;
  readonly equipped: boolean;
  readonly price: number;
  readonly rarity: (typeof BALL_SKIN_CATALOG)[BallSkinId]['rarity'];
  readonly art: ComponentChildren;
}
